/**
 * @fileoverview Session Templates API Endpoints
 * @author Hospital Room Scheduler Team
 * @created 10-10-2025 19:13:45
 * 
 * API client functions for global session template management.
 * Handles HTTP requests to backend session template endpoints with proper error handling.
 * 
 * Features:
 * - Session template CRUD operations (global, no room-specific)
 * - Room-template association management
 * - Proper TypeScript types
 * - Error handling and validation
 */

import { api } from '../index';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Session template data structure (global, no roomId)
 */
export interface SessionTemplate {
  id: string;
  name: string;
  description?: string;
  startTime: string;
  endTime: string;
  bufferMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    roomTemplates: number;
    assignments: number;
  };
}

/**
 * Create session template data
 */
export interface CreateSessionTemplateData {
  name: string;
  description?: string;
  startTime: string;
  endTime: string;
  bufferMinutes: number;
  isActive: boolean;
}

/**
 * Update session template data
 */
export interface UpdateSessionTemplateData {
  name?: string;
  description?: string;
  startTime?: string;
  endTime?: string;
  bufferMinutes?: number;
  isActive?: boolean;
}

/**
 * Query parameters for fetching session templates
 */
export interface GetSessionTemplatesParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'startTime' | 'endTime' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated session templates response
 */
export interface PaginatedSessionTemplatesResponse {
  data: SessionTemplate[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Session template statistics
 */
export interface SessionTemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  inactiveTemplates: number;
  templatesWithRooms: number;
  templatesWithoutRooms: number;
  averageRoomsPerTemplate: number;
  topTemplates: {
    id: string;
    name: string;
    roomCount: number;
  }[];
}

/**
 * Room-template assignment data
 */
export interface AssignSessionTemplatesToRoomData {
  sessionTemplateIds: string[];
  markDefaultId?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all session templates with optional filtering and pagination (global)
 * GET /api/v1/session-templates
 */
export const getAllSessionTemplates = async (
  params?: GetSessionTemplatesParams
): Promise<PaginatedSessionTemplatesResponse> => {
  try {
    const response = await api.get('/session-templates', { params });
    return response.data;
  } catch (error: any) {
    console.error('[getAllSessionTemplates] Error:', error);
    throw error?.response?.data || { message: 'Failed to fetch session templates' };
  }
};

/**
 * Get a single session template by ID
 * GET /api/v1/session-templates/:id
 */
export const getSessionTemplateById = async (id: string): Promise<SessionTemplate> => {
  try {
    const response = await api.get(`/session-templates/${id}`);
    return response.data;
  } catch (error: any) {
    console.error('[getSessionTemplateById] Error:', error);
    throw error?.response?.data || { message: 'Failed to fetch session template' };
  }
};

/**
 * Create a new session template (global, no room assignment)
 * POST /api/v1/session-templates
 */
export const createSessionTemplate = async (
  data: CreateSessionTemplateData
): Promise<SessionTemplate> => {
  try {
    const response = await api.post('/session-templates', data);
    return response.data;
  } catch (error: any) {
    console.error('[createSessionTemplate] Error:', error);
    throw error?.response?.data || { message: 'Failed to create session template' };
  }
};

/**
 * Update an existing session template
 * PUT /api/v1/session-templates/:id
 */
export const updateSessionTemplate = async (
  id: string,
  data: UpdateSessionTemplateData
): Promise<SessionTemplate> => {
  try {
    const response = await api.put(`/session-templates/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error('[updateSessionTemplate] Error:', error);
    throw error?.response?.data || { message: 'Failed to update session template' };
  }
};

/**
 * Delete a session template
 * DELETE /api/v1/session-templates/:id
 */
export const deleteSessionTemplate = async (id: string): Promise<void> => {
  try {
    await api.delete(`/session-templates/${id}`);
  } catch (error: any) {
    console.error('[deleteSessionTemplate] Error:', error);
    throw error?.response?.data || { message: 'Failed to delete session template' };
  }
};

/**
 * Get session template statistics
 * GET /api/v1/session-templates/stats
 */
export const getSessionTemplateStats = async (): Promise<SessionTemplateStats> => {
  try {
    const response = await api.get('/session-templates/stats');
    return response.data;
  } catch (error: any) {
    console.error('[getSessionTemplateStats] Error:', error);
    throw error?.response?.data || { message: 'Failed to fetch session template statistics' };
  }
};

// =============================================================================
// ROOM-TEMPLATE ASSOCIATION FUNCTIONS
// =============================================================================

/**
 * Get session templates assigned to a specific room
 * GET /api/v1/rooms/:roomId/session-templates
 */
export const getRoomSessionTemplates = async (roomId: string): Promise<SessionTemplate[]> => {
  try {
    const response = await api.get(`/rooms/${roomId}/session-templates`);
    return response.data;
  } catch (error: any) {
    console.error('[getRoomSessionTemplates] Error:', error);
    throw error?.response?.data || { message: 'Failed to fetch room session templates' };
  }
};

/**
 * Assign session templates to a room
 * POST /api/v1/rooms/:roomId/session-templates
 */
export const assignSessionTemplatesToRoom = async (
  roomId: string,
  data: AssignSessionTemplatesToRoomData
): Promise<any> => {
  try {
    const response = await api.post(`/rooms/${roomId}/session-templates`, data);
    return response.data;
  } catch (error: any) {
    console.error('[assignSessionTemplatesToRoom] Error:', error);
    throw error?.response?.data || { message: 'Failed to assign session templates to room' };
  }
};

/**
 * Remove a session template from a room
 * DELETE /api/v1/rooms/:roomId/session-templates/:templateId
 */
export const removeSessionTemplateFromRoom = async (
  roomId: string,
  templateId: string
): Promise<void> => {
  try {
    await api.delete(`/rooms/${roomId}/session-templates/${templateId}`);
  } catch (error: any) {
    console.error('[removeSessionTemplateFromRoom] Error:', error);
    throw error?.response?.data || { message: 'Failed to remove session template from room' };
  }
};

export default {
  getAllSessionTemplates,
  getSessionTemplateById,
  createSessionTemplate,
  updateSessionTemplate,
  deleteSessionTemplate,
  getSessionTemplateStats,
  getRoomSessionTemplates,
  assignSessionTemplatesToRoom,
  removeSessionTemplateFromRoom
};

